import clsx from 'clsx'
import {useFormik} from 'formik'
import {useState} from 'react'
import {initialValues} from './_module'
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup'
import {useAppDispatch, useAppSelector} from '../../../../../redux/hooks'
import {updateUserPassword, reset} from '../../../../../redux/system/user/userSlice'
import {showMessage} from '../../../../../_metronic/helpers/swalHelper'

const ChangePassword = () => {
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)

  const dispatch = useAppDispatch()

  const {errors} = useAppSelector((state) => state.user)

  // Form Validation
  const passwordSchema = Yup.object().shape({
    old_password: Yup.string().required(
      t('VALIDATION.REQUIRED_', {name: t('GLOBAL.OLD_PASSWORD')})
    ),

    password: Yup.string()
      .min(8, t('VALIDATION.MIN_CHARACTER_', {name: t('GLOBAL.PASSWORD'), quantity: 8}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.PASSWORD')})),

    password_confirmation: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.CONFIRM_PASSWORD')}))
      .oneOf([Yup.ref('password')], t('VALIDATION.NO_MATCH_', {name: t('GLOBAL.PASSWORD')})),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      const formData = new FormData()
      formData.append('old_password', values.old_password)
      formData.append('password', values.password)
      formData.append('password_confirmation', values.password_confirmation)

      // Update User Password
      dispatch(updateUserPassword(formData)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          resetForm()
          dispatch(reset())
          showMessage(t('MESSAGES.UPDATED'), 'success')
        }

        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  return (
    <div className='menu menu-sub menu-sub-dropdown w-250px w-md-300px' data-kt-menu='true'>
      <div className='px-7 py-5'>
        <div className='fs-5 text-dark fw-bolder'>{t('PROFILE.CHANGE_PASSWORD')}</div>
      </div>

      <div className='separator border-gray-200'></div>

      <div className='px-7 py-5'>
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          autoComplete='off'
          onSubmit={formik.handleSubmit}
        >
          <div className='mb-1'>
            <label className='required'>{t('GLOBAL.OLD_PASSWORD')}</label>
            <input
              type='password'
              placeholder={t('GLOBAL.OLD_PASSWORD')}
              {...formik.getFieldProps('old_password')}
              className={clsx(
                'form-control form-control-sm',
                {
                  'is-invalid':
                    (formik.touched.old_password && formik.errors.old_password) ||
                    errors.old_password,
                },
                {
                  'is-valid': formik.touched.old_password && !formik.errors.old_password,
                }
              )}
            />
            {formik.touched.old_password && formik.errors.old_password && (
              <div className='text-danger'>{formik.errors.old_password}</div>
            )}
            {errors.old_password && (
              <div className='text-danger'>
                {t('VALIDATION.NO_MATCH_', {
                  name: t('GLOBAL.OLD_PASSWORD'),
                })}
              </div>
            )}
          </div>
          <div className='mb-1'>
            <label className='required'>{t('GLOBAL.PASSWORD')}</label>
            <input
              type='password'
              placeholder={t('GLOBAL.PASSWORD')}
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-sm',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='text-danger'>{formik.errors.password}</div>
            )}
          </div>
          <div className='mb-1'>
            <label className='required'>{t('GLOBAL.CONFIRM_PASSWORD')}</label>
            <input
              type='password'
              placeholder={t('GLOBAL.CONFIRM_PASSWORD')}
              {...formik.getFieldProps('password_confirmation')}
              className={clsx(
                'form-control form-control-sm',
                {
                  'is-invalid':
                    formik.touched.password_confirmation && formik.errors.password_confirmation,
                },
                {
                  'is-valid':
                    formik.touched.password_confirmation && !formik.errors.password_confirmation,
                }
              )}
            />
            {formik.touched.password_confirmation && formik.errors.password_confirmation && (
              <div className='text-danger'>{formik.errors.password_confirmation}</div>
            )}
          </div>

          <div className='d-flex justify-content-start py-5'>
            <button
              type='submit'
              className='btn btn-sm btn-light btn-active-primary fw-bolder'
              disabled={formik.isSubmitting || !formik.isValid || loading}
            >
              {!loading && (
                <span className='indicator-label'>
                  <i className='fas fa-save mx-2'></i>
                  {t('BUTTONS.UPDATE')}
                </span>
              )}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {t('GLOBAL.LOADING')}
                  <span className='spinner-border spinner-border-sm align-middle ms-2 mx-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ChangePassword
